import { createContext, useContext, useEffect, useState } from 'react';
import type { SeasonDefinition } from './types';

export interface SeasonState {
  /** The season being shown right now (null when none, or the visitor turned it off). */
  season: SeasonDefinition | null;
  /** The season the site is running, before the visitor's preference. */
  available: SeasonDefinition | null;
  /** False when the visitor has turned the current season off. */
  effectsOn: boolean;
  setEffectsOn: (on: boolean) => void;
  /** Raster pieces whose files loaded (of those requested), piece name to URL. */
  art: Record<string, string>;
  /** Registers a piece as on screen; returns its release. */
  requestArt: (piece: string) => () => void;
}

const NO_ART: Record<string, string> = {};

export const SeasonContext = createContext<SeasonState>({
  season: null,
  available: null,
  effectsOn: true,
  setEffectsOn: () => {},
  art: NO_ART,
  requestArt: () => () => {},
});

export function useSeason(): SeasonState {
  return useContext(SeasonContext);
}

/** The active season's copy for `key`, else the page's own `fallback`. */
export function useSeasonCopy(key: string, fallback: string): string {
  const { season } = useContext(SeasonContext);
  return season?.copy?.[key] ?? fallback;
}

/**
 * The URL of a raster piece of the active season once its file has loaded,
 * else undefined. Calling this is what fetches the piece: it is requested
 * while the caller is mounted and `enabled` (pass false where the piece would
 * be hidden, e.g. by a breakpoint), and never otherwise.
 */
export function useSeasonArt(piece: string, enabled = true): string | undefined {
  const { season, art, requestArt } = useContext(SeasonContext);
  const exists = !!season?.art?.[piece];
  useEffect(() => {
    if (!enabled || !exists) return;
    return requestArt(piece);
  }, [piece, enabled, exists, requestArt]);
  return enabled ? art[piece] : undefined;
}

/** Whether a media query matches, kept current. */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(query).matches : false,
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const sync = () => setMatches(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, [query]);
  return matches;
}
